import {
  generic_get_network_request,
  generic_post_network_request,
  generic_delete_network_request,
} from '../axiosClient/axiosClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AssignForm_Payload_Type {
  connection_id: number;
  user_ids: number[];
  due_date?: string | null;
  assignedViaRoleId?: number;
  file_name?: string;
  department_name?: string;
}

export interface AssignForm_Response_Type {
  data: {
    assignment_id: number;
    user_id: number;
    email_id: string;
    first_name: string;
    last_name: string;
    file_id: number;
    s3_path: string;
  }[];
}

export interface GetFormAssignment_Type {
  assignment_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email_id: string;
  status: string;
  assigned_at: string;
  due_date: string | null;
  completed_at: string | null;
  assignedViaRoleId: number | null;
  submitted: boolean;
  submitted_at: string | null;
  submission_s3_path: string | null;
}

export interface GetFormAssignments_Response_Type {
  data: GetFormAssignment_Type[];
}

export interface MatrixRow_Type {
  assignment_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email_id: string;
  file_id: number;
  file_name: string;
  library_id: number;
  connection_id: number;
  assigned_at: string;
  last_reminder_sent_at: string | null;
  submitted: boolean;
  submitted_at: string | null;
}

export interface GetAcknowledgementMatrix_Response_Type {
  data: MatrixRow_Type[];
}

export interface FormSubmission_Type {
  submission_id: number;
  // Nullable on older/migrated rows — guard before use.
  assignment_id: number | null;
  user_id: number;
  email_id: string;
  file_name: string;
  company_name: string;
  // Nullable on older/migrated rows — guard before formatting.
  submitted_at: string | null;
  // Short-lived (~30 min) presigned S3 URL; "" when no file is stored.
  // Never cache/persist — re-fetch the list to get fresh URLs.
  submission_url: string;
}

export interface GetFormSubmissions_Response_Type {
  data: FormSubmission_Type[];
}

export interface ArchivedSubmission_Type {
  submission_id: number;
  // Nullable ISO string — guard before formatting.
  submitted_at: string | null;
  // Short-lived (~30 min) presigned S3 URL; "" when no file is available (disable link).
  submission_url: string;
  email_id: string;
  file_name: string;
  company_name: string;
  // Optional department/category label — shown as a chip on the Files card when provided.
  department?: string;
  // Independent flags — either or both may be true. Drive the tab membership + badges.
  file_deleted: boolean;
  user_deleted: boolean;
}

export interface GetArchivedSubmissions_Response_Type {
  data: ArchivedSubmission_Type[];
}

// ─── API Functions ────────────────────────────────────────────────────────────

export const assignForm = (payload: AssignForm_Payload_Type) =>
  generic_post_network_request<AssignForm_Response_Type, AssignForm_Payload_Type>(
    'form-assignments',
    payload
  );

export const getFormAssignments = (connection_id: number) => {
  if (!connection_id || connection_id <= 0) return Promise.resolve({ data: [] } as GetFormAssignments_Response_Type);
  return generic_get_network_request<GetFormAssignments_Response_Type>(
    `form-assignments?connection_id=${connection_id}`
  );
};

export const getFormSubmissions = (connection_id: number) => {
  if (!connection_id || connection_id <= 0) return Promise.resolve({ data: [] } as GetFormSubmissions_Response_Type);
  return generic_get_network_request<GetFormSubmissions_Response_Type>(
    `form-assignments/submissions?connection_id=${connection_id}`
  );
};

export const getArchivedSubmissions = (company_id: number) => {
  if (!company_id || company_id <= 0) return Promise.resolve({ data: [] } as GetArchivedSubmissions_Response_Type);
  return generic_get_network_request<GetArchivedSubmissions_Response_Type>(
    `form-assignments/archived-submissions?company_id=${company_id}`
  );
};

export const getAcknowledgementMatrix = (company_id: number) =>
  generic_get_network_request<GetAcknowledgementMatrix_Response_Type>(
    `acknowledgement-matrix/${company_id}`
  );

export const sendReminder = (assignment_id: number) =>
  generic_post_network_request<{ message: string; assigned_at: string }, Record<string, never>>(
    `form-assignments/${assignment_id}/remind`,
    {}
  );

export const unassignForm = (assignment_id: number) =>
  generic_delete_network_request<{ msg: string }>(
    `form-assignments/${assignment_id}`
  );
